// /meet — single page that hosts list, in-call, and transcript views.
// Sub-views are state-driven via root.meetView ('list' | 'call' | 'transcript').
// URL hash mirrors state (#room=<uuid>&view=call|transcript) for share-link
// + back-button support. Same convention chat uses for #channel/#thread/#tab.
//
// **LiveKit Room lifecycle is imperative, not reactive.** Click handlers on
// MeetRoomRow / Main.Join / NewRoomModal submit call `connectCall`, and
// ControlBar.Leave calls `disconnectCall`. DOMQL v3.14 doesn't fire onUpdate
// reliably on root state changes and doesn't fire onCreate on `if:`/`extends:`
// children, so reactive lifecycle isn't an option. Chat / calendar follow
// the same pattern (imperative action functions driven by click handlers).
import { appPageBoilerplateFetch } from '../functions/pageBoilerplate.js'
import { rowToRoom } from '../functions/meet/meetUtils.js'
import { rowToColumn } from '../functions/tickets/ticketUtils.js'

export const home = {
  extends: 'AppPage',
  metadata: { title: 'Meet / Symbols Workspace' },

  fetch: [
    ...appPageBoilerplateFetch,
    {
      method: 'select',
      from: 'workspaceProject.meet.rooms',
      order: 'created_at',
      ascending: false,
      as: '~/rooms',
      cache: '15s',
      transform: (rows) => ({ rooms: (rows || []).map(rowToRoom) })
    },
    // Ticket columns — needed so TicketsDetailModal shows column labels
    // when the user creates a ticket from a meet suggestion. Lightweight
    // reference data; cached for 5 minutes.
    {
      method: 'select',
      from: 'workspaceProject.ticketColumns',
      order: 'position',
      as: '~/columns',
      cache: '5m',
      transform: (rows) => ({ columns: (rows || []).map(rowToColumn) })
    },
    // Initial load of pending waiting-room rows so the host sees guests
    // who requested while the page was closed/reloading. Realtime keeps it
    // fresh after this. No filter — host may own multiple rooms.
    {
      method: 'select',
      from: 'workspaceProject.meet.waitingRoom',
      as: '~/waitingRoom',
      transform: (rows) => {
        const map = {}
        for (const r of (rows || [])) {
          const g = {
            id: r.id,
            roomId: r.room_id,
            guestName: r.guest_name || 'Guest',
            guestEmail: r.guest_email || null,
            requestedAt: r.requested_at,
            admittedAt: r.admitted_at,
            rejectedAt: r.rejected_at
          }
          if (!map[g.roomId]) map[g.roomId] = []
          map[g.roomId].push(g)
        }
        return { waitingRoom: map }
      }
    }
  ],

  onCreate: (el, s) => {
    const root = s.root

    // Seed state from URL hash on mount. #room=<uuid>&view=call → activeRoomId
    // + meetView get set. If view=call and the hash is fresh (direct navigation
    // or reload while in a call), also fire connectCall to actually join.
    let seedView = 'list'
    let seedRoom = null
    if (typeof window !== 'undefined') {
      const hash = window.location.hash.slice(1)
      const params = new URLSearchParams(hash)
      const r = params.get('room')
      seedRoom = r && r !== 'null' && r !== 'undefined' ? r : null
      const viewParam = params.get('view')
      if (viewParam === 'call' || viewParam === 'transcript') seedView = viewParam
    }
    root.update({
      activeRoomId: seedRoom,
      meetView: seedRoom ? seedView : 'list',
      // Default live captions to visible when transcription is on.
      // Undefined treated as true in the hide: check.
      showLiveCaptions: true,
      // Ensure utterances is an empty array (not undefined) so
      // realtime INSERT events can append without the appenders
      // needing to initialize it first.
      utterances: [],
      // Seed the new-room draft so MeetNewRoomModal's reactive getters
      // (value:, theme:, text:) don't hit `undefined.newRoomDraft` on
      // initial render. The modal stays hidden until showNewRoom=true
      // but its factories are still evaluated for dependency tracking.
      newRoomDraft: { name: '', kind: 'room', transcribeEnabled: true, isPrivate: false }
      // waitingRoom is populated by the fetch block above so we don't
      // reset it here (that would wipe the fetched data).
    })

    // Subscribe to meet_rooms AND meet_participants across ALL rooms (no
    // roomId filter) — the room list needs to show a "LIVE" badge on any
    // room that has unended participants, so we need participant events
    // for every room the user can see. The extra event volume is low:
    // participant insert/update fires only on join/leave.
    // Also subscribe to meet_waiting_room so the host sees pending guest
    // requests across every room they own — the banner needs to appear
    // even if the host isn't already in the call when the guest clicks.
    // TODO[sdk-realtime]: route through SDK realtime once entity subscribe
    // is wired (currently uses direct Supabase realtime channel).
    el.call('subscribeMeetRealtime', {
      tables: ['meet_rooms', 'meet_participants', 'meet_waiting_room']
    })

    if (seedView === 'call' && seedRoom) {
      el.call('connectCall', seedRoom)
    }
  },

  // Re-establish the broad (no-roomId) subscription when returning to the
  // list view. TranscriptPage.onUpdate may have narrowed the scope to a
  // specific roomId; going "Back to calls" doesn't reset it automatically
  // because pages/meet.js has no onUpdate. This ensures the host always
  // receives waiting-room events for ALL their rooms while on list view.
  //
  // Also drives transcript analysis: DOMQL storeUpdate fires onUpdate only
  // on state.__element (the page root), NOT on child components. So
  // TranscriptPage.onUpdate never fires from root state changes — we trigger
  // analysis here instead, mirroring the _tryJoinPresence pattern in
  // pages/calendar.js.
  onUpdate: (el, s) => {
    if (s.root?.meetView === 'list') {
      // TODO[sdk-realtime]: route through SDK realtime once entity subscribe
      // is wired (currently uses direct Supabase realtime channel).
      el.call('subscribeMeetRealtime', {
        tables: ['meet_rooms', 'meet_participants', 'meet_waiting_room']
      })
    }
    if (s.root?.meetView === 'transcript') {
      const root = s.root
      if (!root.activeRoomId) return
      if (root.analysis || root._meetAnalyzeInFlight || root.analyzeError || root.analyzing) return
      // Wait for the cached analysis fetch to resolve before hitting the edge
      // function. `analysis` is `undefined` while the fetch is in-flight and
      // `null` once resolved with no row — firing before resolution produces
      // a redundant round-trip.
      if (root.analysis === undefined) return
      // Trust the server: analyzeTranscript returns 422 for the "no rows yet"
      // case. Don't gate on a client-side utterances filter — that race-
      // conditions against the TranscriptPage fetch resolving.
      root.update({
        analyzing: true,
        transcriptPending: false,
        _meetAnalyzeInFlight: true
      })
      el.call('analyzeTranscript', root.activeRoomId)
        .then((res) =>
          root.update({ analyzing: false, analysis: res, _meetAnalyzeInFlight: false })
        )
        .catch((err) => {
          const reason = err?.message || 'analysis failed'
          const isPending = err?.status === 422 || /no transcript/i.test(reason)
          root.update({
            analyzing: false,
            _meetAnalyzeInFlight: false,
            transcriptPending: isPending ? true : false,
            analyzeError: isPending ? null : reason
          })
        })
    }
  },

  MeetSidebar: {},
  MeetMain: {},
  MeetNewRoomModal: {},
  MeetRoomSettingsModal: {},
  MeetSuggestionConfirmModal: {},
  // Native modals pre-filled by openSuggestionModal when the user clicks
  // a ticket or calendar suggestion on the transcript page.
  TicketsDetailModal: {},
  CalNewBookingModal: {}
}
